import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Alert,
  Button,
  Grid,
  Chip,
} from '@mui/material';
import { MONTHLY_FAX_SUBSCRIPTION } from '../../../../constants/fax-constants';

const StepThree = ({
  assignmentType,
  selectedFacility,
  selectedDepartment,
  facilities,
  themeMode,
  currentTheme,
  selectedNumber,
  purchaseError,
  purchasing,
  setStep,
  handlePurchase,
}) => {

  /**
   * Format phone number from +19075313116 to +1 (907) 531-3116
   */
  const formatPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return 'N/A';
    
    // Remove all non-digits
    const digits = phoneNumber.replace(/\D/g, '');
    
    // Handle different lengths
    if (digits.length === 11 && digits[0] === '1') {
      // Format as +1 (XXX) XXX-XXXX
      return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
    } else if (digits.length === 10) {
      // Assume US number without country code
      return `+1 (${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    
    // For other formats, return as-is
    return phoneNumber;
  };

  // Get assignment details based on assignment type
  const getAssignmentDetails = () => {
    if (assignmentType === 'all') {
      return {
        type: 'all',
        title: 'All Facilities and Departments',
        description: 'This number will be available across your entire organization',
        facilities: facilities,
        totalDepartments: facilities.reduce((sum, f) => sum + (f.departments?.length || 0), 0)
      };
    } else if (assignmentType === 'facility') {
      const facility = facilities.find(f => f._id === selectedFacility);
      if (!facility) return null;
      return {
        type: 'facility',
        title: facility.facilityName,
        description: `All ${facility.departments?.length || 0} departments in this facility`,
        facilities: [facility],
        departments: facility.departments || [],
        totalDepartments: facility.departments?.length || 0
      };
    } else if (assignmentType === 'department') {
      const facility = facilities.find(f => f._id === selectedFacility);
      const department = facility?.departments?.find(d => d._id === selectedDepartment);
      if (!facility || !department) return null;
      return {
        type: 'department',
        title: department.name,
        description: `Department in ${facility.facilityName}`,
        facilities: [facility],
        departments: [department],
        totalDepartments: 1
      };
    }
    return null;
  };

  const assignmentDetails = getAssignmentDetails();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%', maxWidth: 700 }}>
      {/* Header */}
      <Box sx={{ flexShrink: 0, mb: 3 }}>
        <Typography
          variant="h5"
          component="h1"
          fontWeight="bold"
          sx={{
            color: themeMode === 'dark' ? currentTheme.text.primary : "#333333",
            fontSize: '28px',
            mb: 1
          }}
        >
          Review Purchase
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: themeMode === 'dark' ? currentTheme.text.secondary : "#666666",
            fontSize: '16px'
          }}
        >
          Confirm your selections before completing the purchase
        </Typography>
      </Box>

      {/* Scrollable Content */}
      <Box sx={{ flexGrow: 1, overflowY: 'auto', pr: 2 }}>
        {/* Purchase Details */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={4}>
            <Paper
              elevation={0}
              variant="outlined"
              sx={{
                p: 2,
                backgroundColor: themeMode === 'dark' ? currentTheme.homePage.search : "#F5F5F5",
                borderRadius: '8px',
                borderColor: themeMode === 'dark' ? currentTheme.border.primary : "#E0E0E0"
              }}
            >
              <Typography variant="body2" sx={{ color: themeMode === 'dark' ? currentTheme.text.secondary : "#666666" }}>
                Fax Number
              </Typography>
              <Typography variant="h6" fontWeight="bold" sx={{ color: currentTheme.text.primary }}>
                {formatPhoneNumber(selectedNumber)}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Paper
              elevation={0}
              variant="outlined"
              sx={{
                p: 2,
                backgroundColor: themeMode === 'dark' ? currentTheme.homePage.search : "#F5F5F5",
                borderRadius: '8px',
                borderColor: themeMode === 'dark' ? currentTheme.border.primary : "#E0E0E0"
              }}
            >
              <Typography variant="body2" sx={{ color: themeMode === 'dark' ? currentTheme.text.secondary : "#666666" }}>
                Monthly Subscription
              </Typography>
              <Typography variant="h6" fontWeight="bold" sx={{ color: currentTheme.text.primary }}>
                ${MONTHLY_FAX_SUBSCRIPTION.toFixed(2)}/month
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* Assignment Section */}
        <Box>
          <Typography variant="body2" fontWeight="bold" sx={{ mb: 2, color: currentTheme.text.primary }}>
            Assignment
          </Typography>
          {!assignmentDetails ? (
            <Typography variant="body2" sx={{ color: themeMode === 'dark' ? currentTheme.text.secondary : "#666666" }}>
              No assignment selected
            </Typography>
          ) : assignmentType === 'all' ? (
            <Paper
              elevation={0}
              variant="outlined"
              sx={{
                p: 3,
                backgroundColor: themeMode === 'dark' ? currentTheme.homePage.search : "#F5F5F5",
                borderRadius: '8px',
                borderColor: themeMode === 'dark' ? currentTheme.border.primary : "#E0E0E0",
              }}
            >
              <Typography variant="body1" fontWeight="bold" sx={{ mb: 0.5, color: currentTheme.text.primary }}>
                {assignmentDetails.title}
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, color: themeMode === 'dark' ? currentTheme.text.secondary : "#666666" }}>
                {assignmentDetails.description}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <Chip
                  label={`${assignmentDetails.facilities.length} ${assignmentDetails.facilities.length === 1 ? 'facility' : 'facilities'}`}
                  sx={{
                    backgroundColor: themeMode === 'dark' ? currentTheme.common.black : "#E0E0E0",
                    color: themeMode === 'dark' ? currentTheme.text.primary : "#333333",
                    borderRadius: '6px',
                    fontSize: '12px',
                  }}
                />
                <Chip
                  label={`${assignmentDetails.totalDepartments} ${assignmentDetails.totalDepartments === 1 ? 'department' : 'departments'}`}
                  sx={{
                    backgroundColor: themeMode === 'dark' ? currentTheme.common.black : "#E0E0E0",
                    color: themeMode === 'dark' ? currentTheme.text.primary : "#333333",
                    borderRadius: '6px',
                    fontSize: '12px',
                  }}
                />
              </Box>
            </Paper>
          ) : assignmentType === 'facility' ? (
            <Paper
              elevation={0}
              variant="outlined"
              sx={{
                p: 3,
                backgroundColor: themeMode === 'dark' ? currentTheme.homePage.search : "#F5F5F5",
                borderRadius: '8px',
                borderColor: themeMode === 'dark' ? currentTheme.border.primary : "#E0E0E0",
              }}
            >
              <Typography variant="body1" fontWeight="bold" sx={{ mb: 0.5, color: currentTheme.text.primary }}>
                {assignmentDetails.title}
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, color: themeMode === 'dark' ? currentTheme.text.secondary : "#666666" }}>
                {assignmentDetails.description}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {assignmentDetails.departments.map((dept, index) => (
                  <Chip
                    key={index}
                    label={dept.name}
                    sx={{
                      backgroundColor: themeMode === 'dark' ? currentTheme.common.black : "#E0E0E0",
                      color: themeMode === 'dark' ? currentTheme.text.primary : "#333333",
                      borderRadius: '6px',
                      fontSize: '12px',
                    }}
                  />
                ))}
              </Box>
            </Paper>
          ) : assignmentType === 'department' ? (
            <Paper
              elevation={0}
              variant="outlined"
              sx={{
                p: 3,
                backgroundColor: themeMode === 'dark' ? currentTheme.homePage.search : "#F5F5F5",
                borderRadius: '8px',
                borderColor: themeMode === 'dark' ? currentTheme.border.primary : "#E0E0E0",
              }}
            >
              <Typography variant="body1" fontWeight="bold" sx={{ mb: 0.5, color: currentTheme.text.primary }}>
                {assignmentDetails.title}
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, color: themeMode === 'dark' ? currentTheme.text.secondary : "#666666" }}>
                {assignmentDetails.description}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <Chip
                  label={assignmentDetails.facilities[0]?.facilityName}
                  sx={{
                    backgroundColor: themeMode === 'dark' ? currentTheme.common.black : "#E0E0E0",
                    color: themeMode === 'dark' ? currentTheme.text.primary : "#333333",
                    borderRadius: '6px',
                    fontSize: '12px',
                  }}
                />
              </Box>
            </Paper>
          ) : null}
        </Box>

        {/* Error Display */}
        {purchaseError && (
          <Alert severity="error" sx={{ mt: 3 }}>
            {purchaseError}
          </Alert>
        )}
      </Box>

      {/* Action Buttons */}
      <Box display="flex" justifyContent="start" mt={4} pt={2} gap={2} sx={{ flexShrink: 0 }}>
        <Button
          variant="outlined"
          onClick={() => setStep(2)}
          disabled={purchasing}
          sx={{
            textTransform: 'none',
            borderRadius: '8px',
            borderColor: currentTheme.border.primary,
            color: currentTheme.text.primary,
            '&:hover': {
              backgroundColor: themeMode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
              borderColor: currentTheme.border.primary,
            }
          }}
        >
          Back
        </Button>
        <Button
          variant="contained"
          onClick={handlePurchase}
          disabled={purchasing}
          sx={{
            backgroundColor: currentTheme.common.danger,
            color: '#ffffff',
            '&:hover': {
              backgroundColor: '#E55A3D',
            },
            '&.Mui-disabled': {
              backgroundColor: themeMode === 'dark' ? 'rgba(255, 107, 71, 0.2)' : 'rgba(0, 0, 0, 0.12)',
              color: themeMode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.26)',
            },
            textTransform: 'none',
            borderRadius: '8px',
          }}
        >
          {purchasing ? 'Processing...' : 'Confirm Purchase'}
        </Button>
      </Box>
    </Box>
  );
};

export default StepThree;
